import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, shareReplay, tap } from 'rxjs';
import { AppConstants } from 'src/app/commons/app-constants';
import { ListCacheService } from 'src/app/offline/list-cache.service';

export interface QualityGrade {
  id: number;
  code: string;
  label: string;
  sortOrder: number;
}

/** Fixed 4-grade vocabulary (A/B/C/D by default), renamable per tenant. Cached for the session
 *  since the list rarely changes — call refresh() after a rename. Routes through
 *  ListCacheService (offline-cache-backed, same "cache what's been seen" convention as
 *  stock groups/master dropdowns) so the Cascade Sheet's Quality step still has real chips
 *  offline instead of only the "Any Quality" fallback — web-safe by construction. */
@Injectable({ providedIn: 'root' })
export class QualityGradeService {
  private apiUrl = AppConstants.API_URL + 'api/v1/quality-grades';
  private readonly listCacheService = inject(ListCacheService);
  private cache$: Observable<QualityGrade[]> | null = null;

  constructor(private http: HttpClient) {}

  /** When the last load FAILED (ms), 0 = it worked or has not run. */
  private failedAt = 0;
  private static readonly RETRY_AFTER_FAILURE_MS = 30_000;

  /**
   * One shared load for the whole app. 2026-10-02: a failed load used to leave NO cached result (shareReplay forgets errors), so every one of the many
   * screens that ask for the grades fetched again: the server counted 215 calls in a minute against its limit of 200 and then refused all of them
   * (429), which surfaced as "No cached list data available for qualityGrades" after login. Now:
   *  - a failure is not an error for the caller: the list is simply empty (every screen already has its "Any quality" fallback), so nothing is thrown;
   *  - after a failure the app does not ask again for 30 seconds (everyone gets the same empty answer), then it tries once more;
   *  - a success is kept for the session, as before.
   */
  list(): Observable<QualityGrade[]> {
    if (this.cache$ && this.failedAt && Date.now() - this.failedAt > QualityGradeService.RETRY_AFTER_FAILURE_MS) {
      this.cache$ = null;
    }
    if (!this.cache$) {
      this.failedAt = 0;
      this.cache$ = this.listCacheService
        .cacheThrough('qualityGrades', () => this.http.get<QualityGrade[]>(this.apiUrl))
        .pipe(
          map(result => result.data),
          catchError(() => { this.failedAt = Date.now(); return of([] as QualityGrade[]); }),
          shareReplay(1),
        );
    }
    return this.cache$;
  }

  rename(code: string, label: string): Observable<QualityGrade> {
    return this.http.put<QualityGrade>(`${this.apiUrl}/${code}`, { label }).pipe(
      tap(() => { this.cache$ = null; this.failedAt = 0; })
    );
  }

  refresh(): void {
    this.cache$ = null;
    this.failedAt = 0;
  }
}
